import { type FC, type FormEvent, useState } from "react";

import { useQuery } from "@apollo/client/react";

import { faCalendarStar } from "@fortawesome/pro-duotone-svg-icons/faCalendarStar";
import { faIdCardClip } from "@fortawesome/pro-duotone-svg-icons/faIdCardClip";

import {
  MeDocument,
  type ProjectErrors,
  type ProjectFragment,
  type ProjectInput,
  UsersDocument,
} from "@app_schema";

import { Button } from "@styled/button";
import { Form } from "@styled/form";
import { InputField } from "@styled/input_field";
import { Notification } from "@styled/notification";
import { Page } from "@styled/page";
import { SelectField } from "@styled/select_field";
import { Sentence } from "@styled/sentence";
import { TextAreaField } from "@styled/text_area_field";

export const DashboardProjectsForm: FC<{
  project?: ProjectFragment | null;
  loading?: boolean;
  errors?: ProjectErrors | null;
  save(_: ProjectInput): void;
}> = ({ project, loading, errors, save }) => {
  const { data: meData } = useQuery(MeDocument);
  const { data: usersData } = useQuery(UsersDocument);

  const canAssign =
    project?.policy?.assign ?? meData?.me?.projectPolicy?.assign ?? false;
  const users = usersData?.users ?? [];

  const [name, setName] = useState<string>(project?.name ?? "");
  const [description, setDescription] = useState(project?.description ?? "");
  const [claimantDob, setClaimantDob] = useState<string>(
    project?.claimantDob ?? "",
  );
  const [userId, setUserId] = useState<string>(project?.user?.id ?? "");

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!name) return;

    save({
      name,
      description,
      claimantDob: claimantDob || undefined,
      userId: canAssign && userId ? userId : undefined,
    });
  };

  return (
    <Page>
      <Form onSubmit={onSubmit}>
        {errors?.base && (
          <Notification color="rose">
            <Sentence>{errors.base}</Sentence>
          </Notification>
        )}

        <InputField
          required
          id="name"
          name="name"
          type="text"
          label="Name:"
          placeholder="Claimant Name"
          icon={faIdCardClip}
          value={name}
          errors={errors}
          onChange={(event) => setName(event.target.value)}
        />

        <InputField
          id="claimantDob"
          name="claimantDob"
          type="date"
          label="Claimant Date of Birth:"
          icon={faCalendarStar}
          value={claimantDob}
          errors={errors}
          onChange={(event) => setClaimantDob(event.target.value)}
        />

        <TextAreaField
          id="description"
          name="description"
          label="Description:"
          placeholder="Description"
          value={description}
          errors={errors}
          onChange={(event) => setDescription(event.target.value)}
        />

        {canAssign && (
          <SelectField
            id="userId"
            name="userId"
            label="Assign To:"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">Select a user...</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </SelectField>
        )}

        <Button type="submit" loading={loading}>
          Save
        </Button>
      </Form>
    </Page>
  );
};
